import { createAsyncThunk } from '@reduxjs/toolkit'

import {
    PartialUpdateBoothSpaceOrganizerRequest,
    UpdateBoothSpaceOrganizerResponse,
} from 'types/swagger'
import { api } from 'utils'
import { Booth } from './types'
import { showErrorNotification } from '../../notifications/notificationsSlice'

interface BoothSaveProps {
    eventSlug: string
    boothSlug: string
    data: PartialUpdateBoothSpaceOrganizerRequest
}

interface UpdateBoothFileProps {
    eventSlug: string
    boothSlug: string
    id: number
    data: { order?: number; name?: string, embedded_url?: string; support_translate?: boolean;
      is_native?: boolean }
}

interface CreateEmbeddedVideoProps {
    eventSlug: string
    boothSlug: string
    embeddedUrl: string
    name: string
}

interface DeleteBoothFileProps {
    eventSlug: string
    boothSlug: string
    id: number
}

export const fetchBooth = createAsyncThunk(
    'FETCH_BOOTH_CONFIG',
    ({ eventSlug, boothSlug }: { eventSlug: string; boothSlug: string }) => {
        const url = `/events/${eventSlug}/organizer/booths/${boothSlug}/`
        return api.get<Booth>(url)
    }
)

export const saveBooth = createAsyncThunk(
    'SAVE_BOOTH',
    (props: BoothSaveProps) => {
        const { data, eventSlug, boothSlug } = props
        const url = `/events/${eventSlug}/organizer/booths/${boothSlug}/`
        return api.patch<UpdateBoothSpaceOrganizerResponse>(url, data)
    }
)

export const updateVideo = createAsyncThunk(
    'UPDATE_VIDEO',
    (props: UpdateBoothFileProps) => {
        const {
            data, id, eventSlug, boothSlug
        } = props
        const url = `/events/${eventSlug}/organizer/booths/${boothSlug}/videos/${id}/`
        return api.patch(url, data)
    }
)

export const createEmbeddedVideo = createAsyncThunk(
    'CREATE_EMBEDDED_VIDEO',
    (props: CreateEmbeddedVideoProps) => {
        const {
            embeddedUrl, name, eventSlug, boothSlug
        } = props
        const url = `/events/${eventSlug}/organizer/booths/${boothSlug}/videos/embedded/`
        return api.post(url, { embedded_url: embeddedUrl, name })
    }
)

export const updateDocument = createAsyncThunk(
    'UPDATE_DOCUMENT',
    (props: UpdateBoothFileProps) => {
        const {
            data, id, eventSlug, boothSlug
        } = props
        const url = `/events/${eventSlug}/organizer/booths/${boothSlug}/documents/${id}/`
        return api.patch(url, data)
    }
)

export const deleteVideo = createAsyncThunk(
    'DELETE_DOCUMENT',
    (props: DeleteBoothFileProps) => {
        const {
            id, eventSlug, boothSlug
        } = props
        const url = `/events/${eventSlug}/organizer/booths/${boothSlug}/videos/${id}/`
        return api.delete(url)
    }
)

export const deleteDocument = createAsyncThunk(
    'DELETE_DOCUMENT',
    (props: DeleteBoothFileProps) => {
        const {
            id, eventSlug, boothSlug
        } = props
        const url = `/events/${eventSlug}/organizer/booths/${boothSlug}/documents/${id}/`
        return api.delete(url)
    }
)

export type UploadType =
    'video'
    | 'document'
    | 'company_logo'
    | 'booth_logo'
    | 'stand_image'

interface BoothUploadProps {
    type: UploadType,
    eventSlug: string
    boothSlug: string
    file: File
    filename?: string // only applies to video and document
    supportTranslate?: boolean
    isNative?: boolean
}

const typeToUploadPath: Record<UploadType, string> = {
    video: 'videos',
    document: 'documents',
    company_logo: 'company_logo',
    booth_logo: 'booth_logo',
    stand_image: 'stand_image',
}

export const upload = createAsyncThunk(
    'BOOTH_UPLOAD',
    async (props: BoothUploadProps, thunkApi) => {
        const {
            type, file, eventSlug, boothSlug, filename = '', supportTranslate = false, isNative = false
        } = props
        const formData = new FormData()
        formData.append('file', file)
        formData.append('support_translate', `${supportTranslate}`)
        formData.append('is_native', `${isNative}`)
        const path = typeToUploadPath[type]
        const url = `/events/${eventSlug}/organizer/booths/${boothSlug}/${path}/?filename=${filename}`
        const response = await api.safePost(url, formData)
        if (response.error) {
            thunkApi.dispatch(showErrorNotification(response.error))
            thunkApi.rejectWithValue(response)
        } else {
            thunkApi.dispatch(fetchBooth({
                eventSlug, boothSlug
            }))
        }
        return response
    },
)
